/**
 * 群组权限管理工具类
 * 用于处理群组权限的字符串并进行权限验证
 */

/**
 * 群组权限常量定义
 * 从右往左，从0开始计数
 */
export const GROUP_PERMISSIONS = {
  SCREENSHOT_ALLOWED: 0, // 允许截屏
  ALL_SPEAK: 1, // 群成员(全部)可发言
  PROFILE_VIEW_ALLOWED: 2, // 群成员可互看资料(群chatroom中可点击其他用户头像)
  MEMBER_LIST_VISIBLE: 3, // 群成员列表可见(列表显示所有成员)
  SEND_CARD_ALLOWED: 4, // 群成员可发送名片
  SEND_IMAGE_ALLOWED: 5, // 群成员可发送图片
  SEND_VIDEO_ALLOWED: 6, // 群成员可发送视频
  SEND_FILE_ALLOWED: 7, // 群成员可发送文件
} as const;

/**
 * 权限中文名称映射
 */
export const PERMISSION_NAMES_CN = {
  SCREENSHOT_ALLOWED: '允许截屏',
  ALL_SPEAK: '群成员可发言',
  PROFILE_VIEW_ALLOWED: '群成员可互看资料',
  MEMBER_LIST_VISIBLE: '群成员列表可见',
  SEND_CARD_ALLOWED: '群成员可发送名片',
  SEND_IMAGE_ALLOWED: '群成员可发送图片',
  SEND_VIDEO_ALLOWED: '群成员可发送视频',
  SEND_FILE_ALLOWED: '群成员可发送文件',
} as const;

/**
 * 权限描述映射
 */
export const PERMISSION_DESCRIPTIONS = {
  SCREENSHOT_ALLOWED: '允许截屏',
  ALL_SPEAK: '群成员可发言',
  PROFILE_VIEW_ALLOWED: '群成员可互看资料',
  MEMBER_LIST_VISIBLE: '群成员列表可见',
  SEND_CARD_ALLOWED: '群成员可发送名片',
  SEND_IMAGE_ALLOWED: '群成员可发送图片',
  SEND_VIDEO_ALLOWED: '群成员可发送视频',
  SEND_FILE_ALLOWED: '群成员可发送文件',
} as const;

/**
 * 权限名称类型
 */
export type GroupPermissionName = keyof typeof GROUP_PERMISSIONS;

/**
 * 内容类型
 */
export type ContentType = 'card' | 'image' | 'video' | 'file';

/**
 * 群组权限信息接口
 */
export interface GroupPermissionInfo {
  permissionString: string;
  permissionValue: number;
  binaryString: string;
  grantedPermissions: string[];
  grantedBits: number[];
  totalPermissions: number;
  grantedCount: number;
}

/**
 * 权限详细信息接口
 */
export interface PermissionDetailInfo {
  bit: string;
  nameCN: string;
  description: string;
}

/**
 * 群组权限管理工具类
 */
export class GroupPermissionUtil {
  private static instance: GroupPermissionUtil;

  private constructor() { }

  /**
   * 获取单例实例
   */
  public static getInstance(): GroupPermissionUtil {
    if (!GroupPermissionUtil.instance) {
      GroupPermissionUtil.instance = new GroupPermissionUtil();
    }
    return GroupPermissionUtil.instance;
  }

  /**
   * 将群组权限字符串转换为二进制权限值
   *
   * @param permissionString 后台下发的群组权限字符串，如 "10000100"
   * @returns 对应的权限值，如果转换失败返回0
   */
  public parseGroupPermissionString(permissionString: string): number {
    try {
      // 移除可能的空格和换行符
      const cleanString = permissionString.trim();

      // 将二进制字符串转换为整数
      const permissionValue = parseInt(cleanString, 2);
      console.log(`群组权限字符串 ${permissionString} 转换为权限值: ${permissionValue}`);

      return isNaN(permissionValue) ? 0 : permissionValue;
    } catch (e) {
      console.error(`解析群组权限字符串失败: ${permissionString}, 错误:`, e);
      return 0;
    }
  }

  /**
   * 检查群组是否拥有指定权限
   *
   * @param permissionString 后台下发的群组权限字符串
   * @param permissionName 要检查的权限名称，如 'ALL_SPEAK'
   * @returns 是否拥有该权限
   */
  public hasGroupPermission(
    permissionString: string,
    permissionName: GroupPermissionName
  ): boolean {
    try {
      if (!permissionString) {
        return false;
      }

      // 如果全是1，则返回true
      if (permissionString === '11111111') {
        return true;
      }

      const permissionValue = this.parseGroupPermissionString(permissionString);
      const permissionBit = GROUP_PERMISSIONS[permissionName];

      if (permissionBit === undefined) {
        console.warn(`未知的群组权限名称: ${permissionName}`);
        return false;
      }

      // 使用位运算检查权限
      const hasPermission = (permissionValue & (1 << permissionBit)) !== 0;

      return hasPermission;
    } catch (e) {
      console.error('检查群组权限失败:', e);
      return false;
    }
  }

  /**
   * 检查群组是否拥有指定权限（通过权限位索引）
   *
   * @param permissionString 后台下发的群组权限字符串
   * @param permissionBit 权限位索引，从0开始（最右边第一位）
   * @returns 是否拥有该权限
   */
  public hasGroupPermissionByBit(permissionString: string, permissionBit: number): boolean {
    try {
      if (permissionBit < 0) {
        console.warn(`群组权限位索引不能为负数: ${permissionBit}`);
        return false;
      }

      const permissionValue = this.parseGroupPermissionString(permissionString);

      // 使用位运算检查权限
      const hasPermission = (permissionValue & (1 << permissionBit)) !== 0;
      console.log(`检查群组权限位 ${permissionBit}: ${hasPermission}`);

      return hasPermission;
    } catch (e) {
      console.error('检查群组权限位失败:', e);
      return false;
    }
  }

  /**
   * 获取群组所有拥有的权限列表
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 拥有的权限名称列表
   */
  public getGrantedGroupPermissions(permissionString: string): string[] {
    try {
      const permissionValue = this.parseGroupPermissionString(permissionString);
      const grantedPermissions: string[] = [];

      Object.entries(GROUP_PERMISSIONS).forEach(([permissionName, permissionBit]) => {
        if ((permissionValue & (1 << permissionBit)) !== 0) {
          grantedPermissions.push(permissionName);
        }
      });

      console.log('群组拥有的权限:', grantedPermissions);
      return grantedPermissions;
    } catch (e) {
      console.error('获取群组权限列表失败:', e);
      return [];
    }
  }

  /**
   * 获取群组所有拥有的权限位索引列表
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 拥有的权限位索引列表
   */
  public getGrantedGroupPermissionBits(permissionString: string): number[] {
    try {
      const permissionValue = this.parseGroupPermissionString(permissionString);
      const grantedBits: number[] = [];

      // 检查前8位权限（群组权限总共8个）
      for (let i = 0; i < 8; i++) {
        if ((permissionValue & (1 << i)) !== 0) {
          grantedBits.push(i);
        }
      }

      console.log('群组拥有的权限位:', grantedBits);
      return grantedBits;
    } catch (e) {
      console.error('获取群组权限位列表失败:', e);
      return [];
    }
  }

  /**
   * 检查群组是否拥有多个权限中的任意一个
   *
   * @param permissionString 后台下发的群组权限字符串
   * @param permissionNames 要检查的权限名称列表
   * @returns 是否拥有其中任意一个权限
   */
  public hasAnyGroupPermission(
    permissionString: string,
    permissionNames: GroupPermissionName[]
  ): boolean {
    for (const permissionName of permissionNames) {
      if (this.hasGroupPermission(permissionString, permissionName)) {
        return true;
      }
    }
    return false;
  }

  /**
   * 检查群组是否拥有所有指定权限
   *
   * @param permissionString 后台下发的群组权限字符串
   * @param permissionNames 要检查的权限名称列表
   * @returns 是否拥有所有权限
   */
  public hasAllGroupPermissions(
    permissionString: string,
    permissionNames: GroupPermissionName[]
  ): boolean {
    for (const permissionName of permissionNames) {
      if (!this.hasGroupPermission(permissionString, permissionName)) {
        return false;
      }
    }
    return true;
  }

  /**
   * 将群组权限值转换为二进制字符串
   *
   * @param permissionValue 权限值
   * @param minLength 最小长度，不足时在前面补0
   * @returns 二进制字符串
   */
  public groupPermissionValueToBinaryString(
    permissionValue: number,
    minLength: number = 8
  ): string {
    const binaryString = permissionValue.toString(2);
    return binaryString.padStart(minLength, '0');
  }

  /**
   * 获取群组权限的详细信息
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 权限的详细信息
   */
  public getGroupPermissionInfo(permissionString: string): GroupPermissionInfo {
    try {
      const permissionValue = this.parseGroupPermissionString(permissionString);
      const binaryString = this.groupPermissionValueToBinaryString(permissionValue);
      const grantedPermissions = this.getGrantedGroupPermissions(permissionString);
      const grantedBits = this.getGrantedGroupPermissionBits(permissionString);

      return {
        permissionString,
        permissionValue,
        binaryString,
        grantedPermissions,
        grantedBits,
        totalPermissions: Object.keys(GROUP_PERMISSIONS).length,
        grantedCount: grantedPermissions.length,
      };
    } catch (e) {
      console.error('获取群组权限信息失败:', e);
      return {
        permissionString: '',
        permissionValue: 0,
        binaryString: '00000000',
        grantedPermissions: [],
        grantedBits: [],
        totalPermissions: 0,
        grantedCount: 0,
      };
    }
  }

  /**
   * 获取权限的中文名称
   *
   * @param permissionName 权限名称
   * @returns 权限的中文名称
   */
  public getPermissionNameCN(permissionName: GroupPermissionName): string {
    return PERMISSION_NAMES_CN[permissionName] || permissionName;
  }

  /**
   * 获取权限的描述信息
   *
   * @param permissionName 权限名称
   * @returns 权限的描述信息
   */
  public getPermissionDescription(permissionName: GroupPermissionName): string {
    return PERMISSION_DESCRIPTIONS[permissionName] || '';
  }

  /**
   * 获取所有权限的中文名称和描述
   *
   * @returns 包含所有权限信息的Map
   */
  public getAllPermissionInfo(): Record<string, PermissionDetailInfo> {
    const permissionInfo: Record<string, PermissionDetailInfo> = {};

    Object.entries(GROUP_PERMISSIONS).forEach(([permissionName, permissionBit]) => {
      permissionInfo[permissionName] = {
        bit: permissionBit.toString(),
        nameCN: this.getPermissionNameCN(permissionName as GroupPermissionName),
        description: this.getPermissionDescription(permissionName as GroupPermissionName),
      };
    });

    return permissionInfo;
  }

  /**
   * 检查群组是否允许发送特定类型的内容
   *
   * @param permissionString 后台下发的群组权限字符串
   * @param contentType 内容类型：'card', 'image', 'video', 'file'
   * @returns 是否允许发送
   */
  public canSendContent(permissionString: string, contentType: ContentType): boolean {
    switch (contentType.toLowerCase()) {
      case 'card':
        return this.hasGroupPermission(permissionString, 'SEND_CARD_ALLOWED');
      case 'image':
        return this.hasGroupPermission(permissionString, 'SEND_IMAGE_ALLOWED');
      case 'video':
        return this.hasGroupPermission(permissionString, 'SEND_VIDEO_ALLOWED');
      case 'file':
        return this.hasGroupPermission(permissionString, 'SEND_FILE_ALLOWED');
      default:
        return true;
    }
  }

  /**
   * 检查群组是否允许截屏
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许截屏
   */
  public canTakeScreenshot(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'SCREENSHOT_ALLOWED');
  }

  /**
   * 检查群组是否允许成员发言
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许成员发言
   */
  public canMemberSpeak(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'ALL_SPEAK');
  }

  /**
   * 检查群组是否允许成员查看其他成员资料
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许查看其他成员资料
   */
  public canViewMemberProfile(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'PROFILE_VIEW_ALLOWED');
  }

  /**
   * 检查群组是否允许查看成员列表
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许查看成员列表
   */
  public canViewMemberList(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'MEMBER_LIST_VISIBLE');
  }

  /**
   * 检查群组是否允许发送名片
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许发送名片
   */
  public canSendCard(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'SEND_CARD_ALLOWED');
  }

  /**
   * 检查群组是否允许发送图片
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许发送图片
   */
  public canSendImage(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'SEND_IMAGE_ALLOWED');
  }

  /**
   * 检查群组是否允许发送视频
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许发送视频
   */
  public canSendVideo(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'SEND_VIDEO_ALLOWED');
  }

  /**
   * 检查群组是否允许发送文件
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许发送文件
   */
  public canSendFile(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'SEND_FILE_ALLOWED');
  }

  /**
   * 检查群组是否允许截屏（别名方法）
   *
   * @param permissionString 后台下发的群组权限字符串
   * @returns 是否允许截屏
   */
  public canScreenshot(permissionString: string): boolean {
    return this.hasGroupPermission(permissionString, 'SCREENSHOT_ALLOWED');
  }

  /**
   * 修改群组权限
   *
   * @param permissionString 原权限字符串
   * @param permissionName 权限名称
   * @param value 权限值
   * @returns 修改后的权限字符串
   */
  public changeGroupPermission(
    permissionString: string,
    permissionName: GroupPermissionName,
    value: boolean
  ): string {
    const permissionValue = this.parseGroupPermissionString(permissionString);
    const permissionBit = GROUP_PERMISSIONS[permissionName];

    if (permissionBit === undefined) {
      return permissionString;
    }

    if (value) {
      return this.groupPermissionValueToBinaryString(
        permissionValue | (1 << permissionBit)
      );
    } else {
      return this.groupPermissionValueToBinaryString(
        permissionValue & ~(1 << permissionBit)
      );
    }
  }

  /**
   * 打印群组权限信息到控制台（调试用）
   *
   * @param permissionString 后台下发的群组权限字符串
   */
  public debugPrintGroupPermissions(permissionString: string): void {
    const info = this.getGroupPermissionInfo(permissionString);
    console.log('=== 群组权限信息 ===');
    console.log('原始字符串:', info.permissionString);
    console.log('权限值:', info.permissionValue);
    console.log('二进制:', info.binaryString);
    console.log('拥有权限:', info.grantedPermissions);
    console.log('权限位:', info.grantedBits);
    console.log('总权限数:', info.totalPermissions);
    console.log('已授权数:', info.grantedCount);
    console.log('==================');
  }
}

/**
 * 获取群组权限工具实例
 */
export const groupPermissionUtil = GroupPermissionUtil.getInstance();
